import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";
import type { CameraPlacement, VisionResult } from "@studystraight/shared-types";
import {
  computeConfidence,
  distance,
  type VisionFrameSource,
  type VisionProvider,
  type VisionProviderCapabilities,
} from "@studystraight/vision-core";

import { defaultGlEnvironment, type GlEnvironment, type VisionGlSurface } from "./glSupport.js";
import { toLandmarkMap } from "./landmarkMapping.js";
import { serializeMediaPipeCreation } from "./mediapipeLoaderLock.js";

const WASM_PATH = "/wasm";
const MODEL_PATH = "/models/pose_landmarker_lite.task";

export interface MediaPipeProviderOptions {
  /** Quyết định landmark nào là bắt buộc khi tính `confidence` (§10). */
  placement: CameraPlacement;
  /** Đo thời gian inference — inject để test được và để không đọc `performance` trong core. */
  now?: () => number;
  /** `defaultVisionConfig.gpuInitTimeoutMs`; tách ra để test không phải chờ 8 giây thật. */
  gpuInitTimeoutMs?: number;
  /**
   * Bỏ hẳn nhánh GPU. `useVisionTracking` bật cờ này khi bản GPU đã `Aborted()` ở giữa inference:
   * thử lại GPU lần nữa chỉ abort lại y như vậy.
   */
  forceCpu?: boolean;
  /** Inject để test chạy được trong `environment: "node"` (không có `document`/WebGL). */
  glEnvironment?: GlEnvironment;
}

/** WebGL2 không dùng được → MediaPipe web không chạy được, kể cả CPU delegate. Xem glSupport.ts. */
export class VisionGlUnavailableError extends Error {
  constructor(reason: string) {
    super(`WebGL2 không dùng được: ${reason}`);
    this.name = "VisionGlUnavailableError";
  }
}

/** Đủ để không treo vô hạn nếu caller quên truyền config. */
const DEFAULT_GPU_INIT_TIMEOUT_MS = 8_000;

interface CreatedLandmarker {
  landmarker: PoseLandmarker;
  delegate: "GPU" | "CPU";
}

export interface MediaPipeWebVisionProvider extends VisionProvider {
  /** ms của lần `detect()` gần nhất — nguồn dữ liệu cho FPS meter và DU #1. */
  readonly lastInferenceMs: number;
  /**
   * Bước đang chạy trong `initialize()`.
   *
   * Trên điện thoại không có console, nên khi watchdog báo timeout thì câu duy nhất người kiểm
   * đọc được là `detail` của banner. "initialize() chưa xong sau 35s" không chỉ ra được gì;
   * "đang ở bước tải WASM (GPU)" thì phân biệt ngay mạng chậm với GPU treo.
   */
  readonly initStage: string;
  /**
   * Vì sao delegate lại là cái đang chạy — "WebGL2 đủ điều kiện" hay "thiếu EXT_color_buffer_float".
   *
   * Debug Mode hiện `delegate: CPU` là không đủ để tune ngưỡng ở Bước 17: CPU vì máy yếu và CPU vì
   * GL của máy hỏng là hai kết luận khác nhau hoàn toàn về thiết bị.
   */
  readonly delegateReason: string;
  /**
   * Context GL đã mất **sau** khi init xong (`webglcontextlost`).
   *
   * Có nó thì `Aborted()` giữa inference không còn là một dòng trần: "abort + context lost" là GPU
   * bị thu hồi giữa phiên (máy nóng, Safari khởi động lại GPU process), còn "abort + context còn
   * sống" là lỗi trong graph — hai việc phải làm khác nhau.
   */
  readonly glContextLost: boolean;
}

/**
 * Adapter MediaPipe cho web — Prompt.md §3, §5.
 *
 * Tầng trên chỉ thấy `VisionProvider`; không có bất kỳ import `@mediapipe/*` nào ngoài
 * thư mục này (ESLint chặn ở packages/*, xem eslint.config.js).
 */
export function createMediaPipeWebVisionProvider(
  options: MediaPipeProviderOptions,
): MediaPipeWebVisionProvider {
  const now = options.now ?? (() => performance.now());
  const gpuInitTimeoutMs = options.gpuInitTimeoutMs ?? DEFAULT_GPU_INIT_TIMEOUT_MS;
  const gl = options.glEnvironment ?? defaultGlEnvironment;
  let landmarker: PoseLandmarker | null = null;
  let delegate: VisionProviderCapabilities["delegate"] = "GPU";
  let lastTimestampMs = -1;
  let lastInferenceMs = 0;
  /** Init đang bay. Giữ lại để `dispose()` đóng được thứ mà `initialize()` chưa kịp nhận. */
  let pendingInit: Promise<CreatedLandmarker> | null = null;
  let disposed = false;
  let initStage = "chưa bắt đầu";
  let delegateReason = "chưa kiểm tra WebGL2";
  /** Canvas + context GL giao cho MediaPipe. Sở hữu ở đây nên `dispose()` phải tháo khỏi DOM. */
  let surface: VisionGlSurface | null = null;

  async function create(withDelegate: "GPU" | "CPU"): Promise<PoseLandmarker> {
    // Xếp hàng: loader của MediaPipe đi qua `self.ModuleFactory` — tạo song song là hỏng cả hai.
    // Xem mediapipeLoaderLock.ts để biết vì sao đây là nguyên nhân của `Aborted()`.
    return serializeMediaPipeCreation(async () => {
      initStage = `tải WASM (${withDelegate})`;
      const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
      initStage = `dựng graph + nạp model (${withDelegate})`;
      return PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate: withDelegate },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputSegmentationMasks: false,
        // Canvas của mình, gắn trong DOM, context đã được đo: không truyền thì Safari 17+ đi
        // nhánh `new OffscreenCanvas(1, 1)` (hàm `Ph()` trong bundle). Xem glSupport.ts.
        ...(surface ? { canvas: surface.canvas } : {}),
      });
    });
  }

  /**
   * GPU trước, CPU sau — nhưng bỏ cuộc GPU theo ĐỒNG HỒ, không chỉ theo exception.
   *
   * `try/catch` quanh `create("GPU")` chỉ bắt được trường hợp promise bị reject. Trên iOS Safari
   * kiểu hỏng hay gặp hơn là **treo**: promise không bao giờ settle, nên fallback CPU không bao
   * giờ chạy và người dùng chỉ thấy "Đang tải bộ nhận diện tư thế…" tới hết `initTimeoutMs`.
   */
  async function createGpuThenCpu(): Promise<CreatedLandmarker> {
    const gpu = create("GPU");
    let gaveUpOnGpu = false;

    // Bỏ cuộc KHÔNG hủy được việc đang chạy trong WASM. Nếu bản GPU về sau khi đã chuyển sang CPU
    // thì phải đóng nó: một PoseLandmarker sống = một wasm module (~11.7 MB) + một WebGL context
    // không ai dùng, và iOS Safari giới hạn cả hai thứ đó chặt hơn desktop nhiều.
    void gpu.then(
      (orphan) => {
        if (gaveUpOnGpu) orphan.close();
      },
      () => undefined,
    );

    let timer: ReturnType<typeof setTimeout> | undefined;
    const giveUp = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        gaveUpOnGpu = true;
        reject(new Error(`GPU delegate chưa khởi tạo xong sau ${String(gpuInitTimeoutMs)}ms`));
      }, gpuInitTimeoutMs);
    });

    try {
      return { landmarker: await Promise.race([gpu, giveUp]), delegate: "GPU" };
    } catch {
      // Một số Android cũ (và WebView) reject ngay ở GPU; iOS thì treo. Hai đường vào cùng một
      // fallback: CPU chậm hơn nhiều nhưng vẫn dùng được, InferenceLoop tự hạ FPS theo p95 (§31).
      //
      // Ca reject: hàng đợi loader đã rảnh, CPU chạy ngay. Ca treo: `create("CPU")` phải đợi bản
      // GPU nhả biến toàn cục ra — chậm, nhưng chen vào thì cả hai cùng chết (mediapipeLoaderLock).
      gaveUpOnGpu = true;
      delegateReason = "GPU không khởi tạo được";
      initStage = "GPU thất bại → đợi loader rảnh rồi thử CPU";
      return { landmarker: await create("CPU"), delegate: "CPU" };
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Quyết định delegate bằng cách **đo trước**, thay vì thử rồi hỏng.
   *
   * Thứ tự ở đây quan trọng: preflight WebGL2 chạy trước khi tạo bất cứ thứ gì. Máy có GL chết thì
   * dừng ngay với một lý do đọc được, chứ không phải đợi hết `initTimeoutMs` rồi abort trong wasm.
   */
  async function createWithFallback(): Promise<CreatedLandmarker> {
    const opened = (surface ??= gl.open());
    const health = opened.health;
    if (!health.ok) {
      // Tháo ngay: canvas chết nằm lại trong DOM chỉ giữ chỗ một context mà máy đang thiếu.
      releaseSurface();
      delegateReason = health.reason;
      initStage = "kiểm tra WebGL2";
      throw new VisionGlUnavailableError(health.reason);
    }

    if (options.forceCpu === true) {
      delegateReason = "buộc dùng CPU sau khi bản GPU abort giữa inference";
      return { landmarker: await create("CPU"), delegate: "CPU" };
    }
    if (!health.floatRenderTarget) {
      // Không thử GPU cho biết: nhánh GPU trên GL thiếu float target là nhánh hay **treo**, mà treo
      // thì chặn luôn hàng đợi loader nên cả CPU cũng không dựng được nữa.
      delegateReason = `bỏ qua GPU vì ${health.note}`;
      initStage = `bỏ qua GPU (${health.note}) → CPU`;
      return { landmarker: await create("CPU"), delegate: "CPU" };
    }

    delegateReason = health.note;
    return createGpuThenCpu();
  }

  function releaseSurface(): void {
    const owned = surface;
    surface = null;
    owned?.remove();
  }

  return {
    get capabilities(): VisionProviderCapabilities {
      return {
        supportsWorldLandmarks: true,
        delegate,
        delegateNote: delegateReason,
        engine: "mediapipe-tasks-vision/pose_landmarker_lite",
      };
    },

    get lastInferenceMs() {
      return lastInferenceMs;
    },

    get initStage() {
      return initStage;
    },

    get delegateReason() {
      return delegateReason;
    },

    get glContextLost() {
      return surface?.isLost() ?? false;
    },

    async initialize() {
      if (landmarker) return;
      pendingInit ??= createWithFallback();
      const created = await pendingInit;
      // `dispose()` đã chạy trong lúc đợi → KHÔNG đóng ở đây, `dispose()` là chủ sở hữu duy nhất
      // của `pendingInit` và sẽ đóng khi nó về (đóng hai lần thì MediaPipe throw).
      if (disposed) return;
      landmarker = created.landmarker;
      delegate = created.delegate;
      initStage = `sẵn sàng (${created.delegate})`;
    },

    async detect(frame: VisionFrameSource): Promise<VisionResult | null> {
      if (!landmarker) throw new Error("VisionProvider chưa initialize()");
      if (frame.kind !== "video") return null;

      const video = frame.element as HTMLVideoElement;
      if (video.readyState < 2 || video.videoWidth === 0) return null;

      // `detectForVideo` yêu cầu timestamp ĐƠN ĐIỆU TĂNG; truyền lại cùng một giá trị (khi
      // video chưa render frame mới) làm MediaPipe throw. Bỏ frame là hành vi đúng, không phải lỗi.
      const timestampMs = frame.timestampMs;
      if (timestampMs <= lastTimestampMs) return null;
      lastTimestampMs = timestampMs;

      const startedAt = now();
      const result = landmarker.detectForVideo(video, timestampMs);
      lastInferenceMs = now() - startedAt;

      const poseLandmarks = result.landmarks[0];
      if (!poseLandmarks) return null;

      const landmarks = toLandmarkMap(poseLandmarks);
      const frameAspectRatio = video.videoWidth / video.videoHeight;

      const vision: VisionResult = {
        timestamp: timestampMs,
        landmarks,
        frameAspectRatio,
        confidence: computeConfidence(landmarks, options.placement),
      };

      // DU #2: tín hiệu chính của `distanceRatio`. Tính ở đây (nơi biết aspect ratio thật)
      // thay vì trong engine, để engine không phải biết gì về khung ảnh.
      const { leftEar, rightEar } = landmarks;
      if (leftEar && rightEar) {
        vision.faceScale = distance(leftEar, rightEar, frameAspectRatio);
      }

      const world = result.worldLandmarks[0];
      if (world) vision.worldLandmarks = toLandmarkMap(world);

      return vision;
    },

    /**
     * Rò rỉ ở đây làm camera vẫn sáng đèn sau khi kết thúc session → mất lòng tin privacy (§7).
     *
     * Phải xử lý được ca `dispose()` chạy **trong lúc `initialize()` còn đang bay**. Đây không
     * phải ca hiếm: StrictMode mount effect hai lần trong dev (mount → cleanup → mount), mà init
     * mất vài giây, nên cleanup LUÔN chạy trước khi landmarker đầu tiên kịp về. Bản cũ chỉ làm
     * `landmarker?.close()` với `landmarker` còn null → instance đó không bao giờ được đóng: mỗi
     * lần vào một screen là rò một wasm module + một WebGL context. Desktop chịu được; iPhone thì
     * lần init thứ hai phải chen chỗ với cái đầu tiên và đó là lúc "không tải được" xuất hiện.
     */
    async dispose() {
      disposed = true;
      lastTimestampMs = -1;
      const pending = pendingInit;
      pendingInit = null;
      const created = landmarker;
      landmarker = null;

      if (created) {
        created.close();
        releaseSurface();
        return;
      }
      // Chưa nhận được landmarker nào: đợi init về rồi đóng. Caller gọi `void dispose()` nên nó
      // chạy tiếp ở background — đúng ý, vì việc cần làm nằm ở TƯƠNG LAI chứ không ở hiện tại.
      if (pending) await pending.then(({ landmarker: late }) => late.close(), () => undefined);
      // Tháo canvas SAU khi landmarker đóng: nó còn giữ GL context của chính canvas này.
      releaseSurface();
    },
  };
}
